import React from "react"
import confetti from "canvas-confetti"
import { cn } from "~/lib/utils"

interface ConfettiProps extends React.HTMLAttributes<HTMLCanvasElement> {
  timeout?: number
}

const Confetti = React.forwardRef<HTMLCanvasElement, ConfettiProps>(({ className, timeout = 20, ...props }, ref) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null)
  const confettiInstanceRef = React.useRef<ReturnType<typeof confetti.create> | null>(null)

  React.useImperativeHandle(ref, () => canvasRef.current as HTMLCanvasElement)

  React.useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    confettiInstanceRef.current = confettiInstanceRef.current || confetti.create(canvas, { resize: true })

    const timeoutSeconds = timeout || 20
    const endTime = Date.now() + timeoutSeconds * 1000
    let cancelled = false

    const shootConfetti = async () => {
      while (Date.now() < endTime && !cancelled) {
        if (confettiInstanceRef.current) {
          confettiInstanceRef.current({
            particleCount: 6,
            ticks: 140,
            spread: 100,
            origin: { x: 0.5, y: 0 },
            scalar: 1.15, // Scale up the particle size
          })
        }

        await new Promise((resolve) => requestAnimationFrame(resolve))
      }
    }

    shootConfetti()

    return () => {
      cancelled = true
    }
  }, [timeout])

  return <canvas ref={canvasRef} className={cn("confetti-canvas", className)} {...props} />
})

Confetti.displayName = "Confetti"

export { Confetti }
